// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

// =============================================================================
// PAYOUTS (seller)
// =============================================================================

// Minimum payout in ngwee (K10). Mirrors mobile `platform_settings.min_payout`
// default. When admin starts overriding this per-platform, read from that table.
const MIN_PAYOUT_NGWEE = 1000;

export function PayoutsPanel({ seller }) {
  const queryClient = useQueryClient();
  const queryKey = ["payouts-panel", seller?.id];
  const [req, setReq] = useState({ amount: "", methodId: "" });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const { data, isLoading: loading } = useQuery({
    queryKey,
    enabled: !!seller?.id,
    staleTime: 30_000,
    queryFn: async () => {
      const [escrowRes, payoutsRes, escrowHeld, methodsRes] = await Promise.all([
        supabase
          .from("escrow_ledger")
          .select("seller_payout, status")
          .eq("seller_id", seller.id)
          .eq("status", "released"),
        supabase
          .from("payouts")
          .select("id, amount, commission_deducted, method, destination, status, period_start, period_end, paid_at, created_at")
          .eq("seller_id", seller.id)
          .order("created_at", { ascending: false })
          .limit(30),
        supabase
          .from("escrow_ledger")
          .select("seller_payout")
          .eq("seller_id", seller.id)
          .eq("status", "held"),
        supabase
          .from("payment_methods")
          .select("id, network, masked_number, is_default, is_verified")
          .eq("user_id", seller.user_id)
          .eq("is_verified", true)
          .order("is_default", { ascending: false })
          .order("created_at", { ascending: false }),
      ]);
      const earned = (escrowRes.data || []).reduce((t, e) => t + (e.seller_payout || 0), 0);
      const paid = (payoutsRes.data || []).filter((p) => ["paid", "completed"].includes(p.status)).reduce((t, p) => t + (p.amount || 0), 0);
      const pending = (payoutsRes.data || []).filter((p) => ["pending", "processing"].includes(p.status)).reduce((t, p) => t + (p.amount || 0), 0);
      const escrow = (escrowHeld.data || []).reduce((t, e) => t + (e.seller_payout || 0), 0);
      const available = Math.max(0, earned - paid - pending);
      return {
        balance: { earned, paid, pending, available, escrow },
        rows: payoutsRes.data || [],
        methods: methodsRes.data || [],
      };
    },
  });

  const balance = data?.balance || { earned: 0, paid: 0, pending: 0, available: 0, escrow: 0 };
  const rows = data?.rows || [];
  const methods = data?.methods || [];

  // Default the dropdown to the seller's default verified method once methods
  // arrive. Stays sticky if the user has already picked one.
  useEffect(() => {
    if (!methods.length) return;
    setReq((r) => {
      if (r.methodId) return r;
      const def = methods.find((m) => m.is_default)?.id || methods[0]?.id || "";
      return { ...r, methodId: def };
    });
  }, [methods]);

  async function submit() {
    const amountKwacha = parseFloat(req.amount);
    if (!amountKwacha || amountKwacha <= 0) {
      setMsg({ tone: "red", text: "Enter an amount." });
      return;
    }
    const amountNgwee = Math.round(amountKwacha * 100);
    if (amountNgwee < MIN_PAYOUT_NGWEE) {
      setMsg({ tone: "red", text: `Minimum payout is ${fmtK(MIN_PAYOUT_NGWEE)}.` });
      return;
    }
    if (amountNgwee > balance.available) {
      setMsg({ tone: "red", text: `Requested amount exceeds available balance (${fmtK(balance.available)}).` });
      return;
    }
    const method = methods.find((m) => m.id === req.methodId);
    if (!method) {
      setMsg({ tone: "red", text: "Pick a payout method." });
      return;
    }

    setSubmitting(true);
    setMsg(null);
    // Layer 3c — block-only path. The atomic request_payout RPC is the
    // only writer for `payouts` rows from a client now (Layer 1's
    // 20260508000001_payout_step_up.sql blocks direct INSERT at the RLS
    // layer). Desktop has no step-up UX yet — no PIN entry on the
    // withdraw flow, no TOTP challenge — so this RPC call will reject:
    //   - sub-threshold      → 'PIN or recovery proof required'
    //   - at/above threshold → 'Step-up authentication required'
    // Both are correct rejections. We surface a redirect message that
    // points sellers at the mobile app, where the full step-up flow
    // ships. The desktop step-up UX is filed as a separate issue.
    const { error } = await supabase.rpc("request_payout", {
      p_amount: amountNgwee,
      p_method: method.network,
      p_destination: method.masked_number,
      // Intentionally no p_step_up_nonce — desktop has no PIN/recovery
      // bridge yet. The RPC will reject. The error mapping below
      // catches the expected rejection codes.
    });
    setSubmitting(false);
    if (error) {
      const msg = error.message || "";
      const isStepUpRejection =
        msg.includes("PIN or recovery proof required") ||
        msg.includes("Step-up authentication required");
      setMsg({
        tone: "red",
        text: isStepUpRejection
          ? "Withdrawals from desktop are temporarily limited. Please use the Kapio mobile app to withdraw — desktop step-up is coming soon."
          : msg || "Payout request failed.",
      });
      return;
    }
    setReq({ amount: "", methodId: req.methodId });
    setMsg({ tone: "green", text: "Payout requested. We'll process it shortly." });
    queryClient.invalidateQueries({ queryKey });
  }

  if (!seller) return <EmptyState icon="banknote" title="No store linked" body="Switch to a seller account." />;
  if (loading) return <Skeleton h={80} />;

  const tint = { pending: C.muted, processing: C.hunter, completed: C.greenDk, paid: C.greenDk, failed: C.red, frozen: "#B45309" };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card pad={22} style={{ background: `linear-gradient(135deg, ${C.hunter}, ${C.hunterLt})`, color: C.dust }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.85 }}>Available to withdraw</div>
        <div style={{ fontSize: 32, fontWeight: 900, marginTop: 6, letterSpacing: "-0.5px" }}>{fmtK(balance.available)}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 18 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.75, textTransform: "uppercase", letterSpacing: 1 }}>Lifetime earned</div>
            <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}>{fmtK(balance.earned)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.75, textTransform: "uppercase", letterSpacing: 1 }}>In escrow</div>
            <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}>{fmtK(balance.escrow)}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.75, textTransform: "uppercase", letterSpacing: 1 }}>Already paid out</div>
            <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}>{fmtK(balance.paid)}</div>
          </div>
        </div>
      </Card>

      <Card pad={22}>
        <Eyebrow>Request a payout</Eyebrow>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>
          Funds unlock 48 hours after delivery, or when the buyer confirms receipt. Minimum payout {fmtK(MIN_PAYOUT_NGWEE)}.
        </div>
        {methods.length === 0 ? (
          <div style={{ marginTop: 12, padding: 14, background: C.redBg, borderRadius: 10, border: `1px solid ${C.red}33`, fontSize: 12.5, color: C.red, fontWeight: 700 }}>
            Add a verified payout method first — open <b>Payout Methods</b> on the left.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
            <Field label="Amount (Kwacha)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={req.amount}
                onChange={(e) => setReq({ ...req, amount: e.target.value })}
                placeholder={`Max ${fmtK(balance.available)}`}
                style={inputStyle}
              />
            </Field>
            <Field label="Payout to">
              <select value={req.methodId} onChange={(e) => setReq({ ...req, methodId: e.target.value })} style={inputStyle}>
                {methods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {({ mtn: "MTN MoMo", airtel: "Airtel Money", zamtel: "Zamtel Money" })[m.network] || m.network} · {m.masked_number}{m.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Btn onClick={submit} disabled={submitting || balance.available < MIN_PAYOUT_NGWEE}>
                {submitting ? "Requesting…" : "Request payout"}
              </Btn>
              <Btn size="sm" kind="outline" onClick={() => setReq({ ...req, amount: (balance.available / 100).toFixed(2) })} disabled={balance.available <= 0}>
                Withdraw all
              </Btn>
              {msg && <span style={{ fontSize: 12, fontWeight: 700, color: msg.tone === "red" ? C.red : C.greenDk }}>{msg.text}</span>}
            </div>
            {balance.available < MIN_PAYOUT_NGWEE && balance.earned > 0 && (
              <div style={{ fontSize: 11.5, color: C.muted }}>
                You need at least {fmtK(MIN_PAYOUT_NGWEE)} to request a payout. Keep selling!
              </div>
            )}
          </div>
        )}
      </Card>

      <Card pad={0}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.cream}` }}>
          <Eyebrow>Payout history</Eyebrow>
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: 22, fontSize: 12.5, color: C.muted }}>No payouts yet.</div>
        ) : (
          <div>
            {rows.map((p) => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 22px", borderBottom: `1px solid ${C.cream}` }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.charcoal }}>{fmtK(p.amount)}</div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                    {p.method} · {p.destination} · {timeAgo(p.created_at)}
                  </div>
                </div>
                <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 8px", borderRadius: 6, background: C.cream, color: tint[p.status] || C.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
